import { useLiveQuery } from "dexie-react-hooks"
import type { AgentExecutionLight } from "@stockline/shared/types/ai/execution"
import { offlineDb } from "../../../lib/offline/db"
import { EXECUTION_TABLE } from "../../../lib/offline/resources/ai/execution/db"
import { isSubagentExecution } from "./isSubagentExecution"

/**
 * Reactive read of the most recent `limit` executions, ordered by
 * `createdAt` desc. Walks the `createdAt` index backwards via a Dexie
 * cursor — only `limit` rows are materialized, regardless of how many
 * total executions exist in the store.
 *
 * Sub-agent rows (forked / dispatched branches) are filtered out so the
 * user-facing list shows only top-level runs; the filter runs during the
 * cursor walk, so a full page of `limit` parent rows is still returned.
 *
 * For infinite scroll: bump `limit` by a page size as the user reaches
 * the bottom. Each re-run reads only the requested rows.
 */
export function useExecutionsPaged(limit: number): AgentExecutionLight[] {
  const result = useLiveQuery(
    () =>
      offlineDb
        .table<AgentExecutionLight>(EXECUTION_TABLE)
        .orderBy("createdAt")
        .reverse()
        .filter((e) => !isSubagentExecution(e))
        .limit(limit)
        .toArray(),
    [limit],
    [] as AgentExecutionLight[]
  )
  return result ?? []
}
